<div class="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-50">
  <!-- Modern Page Header -->
  <div class="bg-white shadow-sm border-b border-slate-200">
    <div class="max-w-7xl mx-auto px-6 py-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-slate-900 mb-2">Audit Reports</h1>
          <p class="text-slate-600 flex items-center gap-2">
            <span nz-icon nzType="file-text" class="text-indigo-500"></span>
            View all completed audits across all auditors
          </p>
        </div>
        <button
          (click)="refresh()"
          class="btn btn-primary btn-md gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          [class.loading]="completedAuditsQuery.isRefetching()"
        >
          <span nz-icon nzType="reload" [nzSpin]="completedAuditsQuery.isRefetching()"></span>
          Refresh
        </button>
      </div>
    </div>
  </div>

  <div class="max-w-7xl mx-auto px-6 py-8">
    <!-- Summary Stats -->
    <div *ngIf="completedAuditsQuery.data() as audits" class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      <div class="stats shadow-lg bg-white border border-slate-200 hover:shadow-xl transition-shadow">
        <div class="stat">
          <div class="stat-figure text-indigo-600">
            <span nz-icon nzType="check-circle" nzTheme="fill" class="text-4xl"></span>
          </div>
          <div class="stat-title text-slate-600">Total Audits</div>
          <div class="stat-value text-indigo-600">{{ getStats().total }}</div>
          <div class="stat-desc text-slate-500">All completed</div>
        </div>
      </div>

      <div class="stats shadow-lg bg-white border border-slate-200 hover:shadow-xl transition-shadow">
        <div class="stat">
          <div class="stat-figure text-green-600">
            <span nz-icon nzType="calendar" class="text-4xl"></span>
          </div>
          <div class="stat-title text-slate-600">Today</div>
          <div class="stat-value text-green-600">{{ getStats().today }}</div>
          <div class="stat-desc text-slate-500">Audits completed</div>
        </div>
      </div>

      <div class="stats shadow-lg bg-white border border-slate-200 hover:shadow-xl transition-shadow">
        <div class="stat">
          <div class="stat-figure text-purple-600">
            <span nz-icon nzType="team" class="text-4xl"></span>
          </div>
          <div class="stat-title text-slate-600">Auditors</div>
          <div class="stat-value text-purple-600">{{ getStats().uniqueAuditors }}</div>
          <div class="stat-desc text-slate-500">Active contributors</div>
        </div>
      </div>

      <div class="stats shadow-lg bg-white border border-slate-200 hover:shadow-xl transition-shadow">
        <div class="stat">
          <div class="stat-figure text-orange-600">
            <span nz-icon nzType="clock-circle" class="text-4xl"></span>
          </div>
          <div class="stat-title text-slate-600">Avg Duration</div>
          <div class="stat-value text-orange-600 text-2xl">{{ formatDuration(getStats().avgDuration) }}</div>
          <div class="stat-desc text-slate-500">Across all audits</div>
        </div>
      </div>
    </div>

    <!-- Search and Filter Controls -->
    <div class="card bg-white shadow-lg mb-6">
      <div class="card-body p-4">
        <div class="flex items-center gap-4 flex-wrap">
          <div class="flex-1 min-w-[300px]">
            <nz-input-group [nzPrefix]="prefixIconSearch">
              <input
                type="text"
                nz-input
                placeholder="Search by audit number, route, bus, or auditor..."
                [(ngModel)]="searchTerm"
                (ngModelChange)="searchTerm.set($event)"
              />
            </nz-input-group>
            <ng-template #prefixIconSearch>
              <span nz-icon nzType="search"></span>
            </ng-template>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-sm text-slate-600">Sort by:</span>
            <nz-select
              [(ngModel)]="sortField"
              (ngModelChange)="sortField.set($event)"
              class="w-40"
            >
              <nz-option nzValue="auditCompletedAt" nzLabel="Date Completed"></nz-option>
              <nz-option nzValue="auditDuration" nzLabel="Duration"></nz-option>
            </nz-select>

            <button
              nz-button
              nzType="default"
              nzSize="small"
              (click)="sortOrder.set(sortOrder() === 'asc' ? 'desc' : 'asc')"
            >
              <span nz-icon [nzType]="sortOrder() === 'asc' ? 'arrow-up' : 'arrow-down'"></span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="completedAuditsQuery.isPending()" class="space-y-4">
      <div class="card bg-white shadow-lg animate-pulse">
        <div class="card-body p-4">
          <div class="h-16 bg-slate-200 rounded"></div>
        </div>
      </div>
      <div class="card bg-white shadow-lg animate-pulse">
        <div class="card-body p-4">
          <div class="h-16 bg-slate-200 rounded"></div>
        </div>
      </div>
      <div class="card bg-white shadow-lg animate-pulse">
        <div class="card-body p-4">
          <div class="h-16 bg-slate-200 rounded"></div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div *ngIf="completedAuditsQuery.isError()">
      <div class="card bg-white shadow-2xl border-2 border-red-200">
        <div class="card-body text-center py-16">
          <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span nz-icon nzType="exclamation-circle" class="text-5xl text-red-500"></span>
          </div>
          <h3 class="text-2xl font-bold text-slate-900 mb-3">
            Failed to Load Audit Reports
          </h3>
          <p class="text-slate-600 mb-8 max-w-md mx-auto">
            There was an error loading the audit reports. Please check your connection and try again.
          </p>
          <button
            (click)="refresh()"
            class="btn btn-error gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <span nz-icon nzType="reload"></span>
            Try Again
          </button>
        </div>
      </div>
    </div>

    <!-- Success State - Audit Reports List -->
    <div *ngIf="completedAuditsQuery.isSuccess()">
      <!-- Empty State -->
      <div *ngIf="getFilteredAudits().length === 0 && searchTerm() === ''">
        <div class="card bg-white shadow-lg">
          <div class="card-body text-center py-16">
            <div class="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span nz-icon nzType="inbox" class="text-6xl text-slate-400"></span>
            </div>
            <h3 class="text-2xl font-semibold text-slate-700 mb-2">No Completed Audits Yet</h3>
            <p class="text-slate-500">Completed audits will appear here once auditors finish their work.</p>
          </div>
        </div>
      </div>

      <!-- No Search Results -->
      <div *ngIf="getFilteredAudits().length === 0 && searchTerm() !== ''">
        <div class="card bg-white shadow-lg">
          <div class="card-body text-center py-12">
            <span nz-icon nzType="search" class="text-5xl text-slate-400 mb-4"></span>
            <h3 class="text-xl font-semibold text-slate-700 mb-2">No Results Found</h3>
            <p class="text-slate-500">Try a different search term</p>
          </div>
        </div>
      </div>

      <!-- Audit Reports Cards -->
      <div *ngIf="getFilteredAudits().length > 0" class="space-y-4">
        <div
          *ngFor="let audit of getFilteredAudits(); let i = index"
          class="card bg-white shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-indigo-500"
        >
          <div class="card-body p-5">
            <div class="flex items-center gap-4">
              <!-- Audit Number Badge -->
              <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <span class="text-white font-bold text-sm">{{ audit.auditNumber }}</span>
              </div>

              <!-- Audit Info -->
              <div class="flex-1 min-w-0">
                <!-- Route Name -->
                <h3 class="text-lg font-bold text-slate-900 mb-2 truncate">{{ audit.routeName }}</h3>

                <!-- Metadata Row -->
                <div class="flex items-center gap-4 text-xs text-slate-600 flex-wrap">
                  <div class="flex items-center gap-1">
                    <span nz-icon nzType="user" class="text-indigo-500"></span>
                    <span class="font-medium">{{ audit.auditStartedByName || 'Unknown' }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <span nz-icon nzType="check-circle" class="text-green-500"></span>
                    <span>Completed {{ audit.auditCompletedAt | amTimeAgo }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <span nz-icon nzType="calendar" class="text-slate-400"></span>
                    <span>{{ audit.auditCompletedAt | date: 'short' }}</span>
                  </div>
                  <div *ngIf="audit.auditDuration" class="flex items-center gap-1">
                    <span nz-icon nzType="clock-circle" class="text-orange-500"></span>
                    <span>Duration: {{ formatDuration(audit.auditDuration) }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <span nz-icon nzType="car" class="text-green-500"></span>
                    <span>{{ audit.busName }}</span>
                  </div>
                </div>
              </div>

              <!-- Status Badge -->
              <div class="flex-shrink-0">
                <span class="badge badge-success badge-lg gap-2">
                  <span nz-icon nzType="check-circle" nzTheme="fill"></span>
                  Completed
                </span>
              </div>

              <!-- View Report Button -->
              <div class="flex-shrink-0">
                <button class="btn btn-ghost btn-sm gap-2" (click)="viewReport(audit)">
                  <span nz-icon nzType="file-text"></span>
                  View Report
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Results Count -->
        <div class="text-center text-sm text-slate-500 py-4">
          Showing {{ getFilteredAudits().length }} of {{ completedAuditsQuery.data().length || 0 }} completed audits
        </div>
      </div>
    </div>
  </div>
</div>

